import { getDictionary } from '@/api/generalApi.js';
import store from '@/store/index';
import { getLocale } from '@/helper/i18n-utils';

export const showNotification = (type, response) => {
    store.dispatch('notifications/showNotification', {
        type,
        response
    });
};

export const currentDateFormat = (dateValue) => {
    let date = new Date(dateValue);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${ year }-${ month }-${ day }`;
};

export const showDate = (dateValue) => {
    return dateValue.split('-').reverse().join('.');
};

export const correctISODate = (dateValue, withTime = true) => {
    const date = new Date(dateValue);

    const options = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    };

    if (withTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }

    return date.toLocaleString('ru-RU', options);
};

export const optimizeForm = (form, correctValue, forEdit = false) => {
    let newForm = [...form];

    if (forEdit) {
        for (let [index, formItem] of newForm.entries()) {
            newForm[index] = formItem.map(item => {
                if (item.new) {
                    return {
                        code: item.idc_code,
                        type: item.type_value,
                        value: correctValue(item.type_value, item.value, item.idc_code)
                    };
                }
                else return {
                    id: item.id,
                    code: !item.id ? item.idc_code : undefined,
                    type: item.type_value,
                    value: correctValue(item.type_value, item.value, item.idc_code)
                };
            });
        }
    }
    else {
        for (let [index, formItem] of newForm.entries()) {
            newForm[index] = formItem.map(item => {
                return {
                    code: item.idc_code,
                    type: item.type_value,
                    value: correctValue(item.type_value, item.value, item.idc_code)
                };
            });
        }
    }
    return newForm;
};

export const makeDataObject = (inputArray, abcCode, resumeId = undefined) => {
    return {
        data: inputArray.map(formItem => ({
            journal: {
                abc_code: abcCode,
                parent_id: resumeId
            },
            indicator: formItem
        }))
    };
};

export const getMatchField = (match, field, withName = false) => {
    const itemValue = match.indicator_value ?? match.value;
    const isDictionary = field.type_value === "dct" || field.type === "dct";
  
    const applyMeta = () => {
        if (withName) field.short_name = match.indicator_meta?.short_name;
        if (match.id) field.id = match.id;
    };
  
    if (isDictionary && itemValue) {
        return getDictionary(match.indicator_meta?.reference ?? field.reference)
            .then(res => {
                if (!res?.data) return;
                field.value = typeof itemValue === "object" && itemValue?.ru
                    ? res.data.find(item => item.short_name[getLocale()] === itemValue[getLocale()])
                    : res.data.find(item => item.id == itemValue) ?? null;
                })
            .finally(applyMeta); 
    } else {
        field.value = itemValue;
        applyMeta();
        return Promise.resolve();
    }
}; 

export const getDataValues = async (form, values, withName = false) => {
    if (!Array.isArray(form) || !form.length || !Array.isArray(values)) return form;
  
    while (form.length < values.length) {
        form.push(JSON.parse(JSON.stringify(form[0])));
    }
  
    const tasks = values.flatMap((group, i) => {
        const formGroup = form[i];
        const filled = group.field_value ?? group.indicator ?? [];
    
        return formGroup.map(field => {
            const match = filled.find(item =>
                item.indicator_meta?.code === field.idc_code 
                    || item.code === field.idc_code 
            );
    
            if (match) {
                return getMatchField(match, field, withName);
            } else {
                field.code = String(field.id);
                field.id = undefined;
                return Promise.resolve();
            }
        });
    });
  
    // если нет данных — сбросить id у всех полей
    if (values.length === 0) {
        form.forEach(group => group.forEach(f => (f.id = undefined)));
    }
  
    await Promise.all(tasks);
    return form;
};
  

export const getLinkWithFile = (response, isBlob = false) => {
    const filenamePart = response.headers.get('Content-Disposition')?.split("filename*=")[1] 
                        ?? response.headers.get('Content-Disposition')?.split("filename=")[1];
    const encodedFilename = filenamePart.includes('utf-8') || filenamePart.includes('UTF-8') 
        ?   filenamePart.split("''")[1] 
        :   null;
    const decodedFilename = filenamePart.includes('utf-8') || filenamePart.includes('UTF-8') 
        ?   decodeURIComponent(encodedFilename) 
        :   filenamePart.replace(/"/g, '');
    const type = response.headers["content-type"];
    
    let blob = isBlob 
        ?   response.data 
        :   new Blob([ response.data ], { type: type });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = decodedFilename ?? 'File';
    link.click();
};

export const quantityWord = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = Math.abs(count) % 100;

    if (lastTwoDigits > 10 && lastTwoDigits < 20) {
        return 2; 
    }
    if (lastDigit === 1) {
        return 0; 
    }
    if (lastDigit > 1 && lastDigit < 5) {
        return 1; 
    }
    return 2; 
};

export const joinTranslations = (data) => {
    return data.reduce((acc, item) => {
        for (const lang in item) {
            acc[lang] = acc[lang] ? `${acc[lang]}, ${item[lang]}` : item[lang];
        }
        return acc;
    }, {});
};

export const splitTranslations = (obj, delimiter = ',') => {
    const entries = Object.entries(obj).map(([lang, str]) => {
        const safeStr = str ?? '';
        return [lang, safeStr.split(delimiter).map(s => s.trim())];
    });
  
    const maxLength = Math.max(...entries.map(([, values]) => values.length));
  
    const result = Array.from({ length: maxLength }, () => ({}));
  
    for (const [lang, values] of entries) {
        values.forEach((value, i) => {
            result[i][lang] = value;
        });
        // Если у этого языка меньше элементов, заполним оставшиеся пустой строкой
        for (let i = values.length; i < maxLength; i++) {
            result[i][lang] = '';
        }
    }
  
    return result;
};  

export const showLocaleValue = (value) => {
    if (!value) return '';
  
    const locale = getLocale();
  
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        if (value[locale]) return value[locale];
        return value.ru ?? value.kk ?? value.en ?? '';
    }
  
    return '';
};

export const getItemName = (item) => {
    if (item.short_name && item.short_name[getLocale()]) return item.short_name[getLocale()]
    else if (item[getLocale()]) return item[getLocale()]
    else if (item.short_name) return item.short_name
    else if (item.name) return item.name 
    else if (item.email) return item.email 
    else return item
};

export const saveResumeInStorage = (key, values) => {
    const processItems = (items) =>
        items.map(item => {
            if (item.idc_code == 2010 || item.idc_code == 2028 
                || item.idc_code == 2106 || item.idc_code == 2154) {
                // Если индикаторы файлов - стираем value
                return { ...item, value: null };
            } 
            if (Array.isArray(item.parameters) && item.parameters.length > 0 && item.type_value === 'dct') {
                return { ...item, parameters: [] };
            }
            return item;
        });
  
    let savedData;
    if (Array.isArray(values[0])) {
        savedData = values.map(subArray => processItems(subArray));
    } else {
        savedData = processItems(values);
    }
  
    localStorage.setItem(key, JSON.stringify(savedData));
};

export const clearSavedResumeInfo = () => {
    const keys = ['resume.general', 'resume.exp', 'resume.edu', 
        'resume.qual', 'resume.lang', 'resume.academ', 
        'resume.other', 'resume.rec', 'resume.pub'];
    for (let key of keys) {
        localStorage.removeItem(key);
    }
};

export const autoFillSection = async (form, storageKey, code, isEditResume) => {
    const normalizedForm = Array.isArray(form[0]) ? form : [form]; // приводим form к массиву массивов
  
    const importedValues = store.state.importData?.hasImport
        ? store.state.importData.imported?.filter(item => item.journal?.abc_code == code)
        : null;
  
    if (importedValues) {
        if (isEditResume) {
            const newForm = await mergeResumeForms(normalizedForm, importedValues);
            if (Array.isArray(form[0])) {
                form.splice(0, form.length, ...newForm);
            } else {
                form.splice(0, form.length, ...newForm[0]);
            }
        } else {
            await getDataValues(normalizedForm, importedValues);
        }
    } else if (!isEditResume) {
        // Creation drafts contain document-field ids. In edit mode those ids
        // would be mistaken for persisted FieldValue ids and overwrite the
        // server-backed form that was loaded immediately before this call.
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
            const parsed = JSON.parse(savedData);
            if (Array.isArray(form)) {
                form.splice(0, form.length, ...parsed);
            }
        }
    }
};  

const fetchHolidays = async (year) => {
    const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${ year }/KZ`);
    if (!response.ok) throw new Error('Ошибка при загрузке праздников');
    const holidays = await response.json();
    return new Set(holidays.map(h => h.date));
};

const isNonWorkingDay = (date, holidaysSet) => {
    const day = date.getDay();
    if (day === 0 || day === 6) return true;
    return holidaysSet.has(date.toISOString().slice(0, 10));
};
  
export const addWorkDays = async (startDate, workDaysToAdd) => {
    const year = startDate.getFullYear();
    const holidays = await fetchHolidays(year);
    const result = new Date(startDate);
    let added = 0;

    while (added < workDaysToAdd) {
        result.setDate(result.getDate() + 1);
        const currentYear = result.getFullYear();
        
        if (currentYear !== year) { // Если перешли на следующий год — обновляем holidays
            holidays.clear();
            (await fetchHolidays(currentYear)).forEach(day => holidays.add(day));
        }

        if (!isNonWorkingDay(result, holidays)) {
            added++;
        }
    }
    return result;
}; 


export const mergeResumeForms = async (arr1, arr2) => {
    const promises = []; 
    const result = JSON.parse(JSON.stringify(arr1));

    const cloneTemplate = () => {
        const template = JSON.parse(JSON.stringify(arr1[0]));
        template.forEach(f => {
            f.id = undefined;
            f.value = undefined;
            f.isUpdated = true;
        });
        return template;
    };
  
    for (const group of arr2) {
        const filled = group.field_value ?? group.indicator ?? [];
        const hasJournalId = group.journal?.id != null;
  
        if (hasJournalId) { // id-based update
            for (const item of filled) {
                if (!item.id) continue;
    
                for (const formGroup of result) {
                    const fld = formGroup.find(f => f.id == item.id);
                    if (fld) {
                        promises.push(getMatchField(item, fld));
                        fld.isUpdated = true;
                        break;
                    }
                }
            }
        } else { // --- code-based update ---
            const template = cloneTemplate();
  
            for (const item of filled) {
                if (!item) continue;
        
                const matchField = template.find(
                    f => f.idc_code === (item.code ?? item.indicator_meta?.code)
                );
        
                if (matchField) {
                    promises.push(getMatchField(item, matchField));
                    matchField.isUpdated = true;
                }
            }
            result.push(template);
        }
    }
  
    await Promise.all(promises);
    return result;
};
